import { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
  Alert,
} from '@mui/material'
import { Add, Edit, Delete, Visibility, CheckCircle, Description } from '@mui/icons-material'
import { PageHeader, Button, DataTable, Card, Modal } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled', color: 'default' },
  { value: 'in_progress', label: 'In Progress', color: 'info' },
  { value: 'completed', label: 'Completed', color: 'warning' },
  { value: 'reported', label: 'Reported', color: 'success' },
  { value: 'approved', label: 'Approved', color: 'primary' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
]

const PRIORITY_OPTIONS = [
  { value: 'routine', label: 'Routine', color: 'default' },
  { value: 'urgent', label: 'Urgent', color: 'warning' },
  { value: 'stat', label: 'STAT', color: 'error' },
]

const MODALITY_OPTIONS = [
  { value: 'xray', label: 'X-Ray' },
  { value: 'ct', label: 'CT Scan' },
  { value: 'mri', label: 'MRI' },
  { value: 'ultrasound', label: 'Ultrasound' },
  { value: 'mammography', label: 'Mammography' },
  { value: 'fluoroscopy', label: 'Fluoroscopy' },
  { value: 'nuclear', label: 'Nuclear Medicine' },
  { value: 'pet', label: 'PET Scan' },
  { value: 'other', label: 'Other' },
]

const ImagingResultsList = () => {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [selectedResult, setSelectedResult] = useState(null)
  const [pendingReportCount, setPendingReportCount] = useState(0)
  const [pendingApprovalCount, setPendingApprovalCount] = useState(0)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    patient_id: '',
    patient_name: '',
    patient_phone: '',
    patient_gender: '',
    study_name: '',
    study_code: '',
    modality: 'xray',
    body_part: '',
    laterality: '',
    priority: 'routine',
    clinical_history: '',
    notes: '',
  })
  const [reportData, setReportData] = useState({
    findings: '',
    impression: '',
    recommendations: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [resultsRes, pendingReportRes, pendingApprovalRes] = await Promise.all([
        api.get('/clinical/imaging-results/'),
        api.get('/clinical/imaging-results/pending_report/'),
        api.get('/clinical/imaging-results/pending_approval/'),
      ])
      setResults(resultsRes.data.results || resultsRes.data || [])
      setPendingReportCount((pendingReportRes.data.results || pendingReportRes.data || []).length)
      setPendingApprovalCount((pendingApprovalRes.data.results || pendingApprovalRes.data || []).length)
    } catch (error) {
      console.error('Error fetching data:', error)
      toast.error('Failed to load imaging results')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAdd = () => {
    setSelectedResult(null)
    setFormData({
      patient_id: '',
      patient_name: '',
      patient_phone: '',
      patient_gender: '',
      study_name: '',
      study_code: '',
      modality: 'xray',
      body_part: '',
      laterality: '',
      priority: 'routine',
      clinical_history: '',
      notes: '',
    })
    setFormOpen(true)
  }

  const handleEdit = (result) => {
    setSelectedResult(result)
    setFormData({
      patient_id: result.patient_id || '',
      patient_name: result.patient_name || '',
      patient_phone: result.patient_phone || '',
      patient_gender: result.patient_gender || '',
      study_name: result.study_name || '',
      study_code: result.study_code || '',
      modality: result.modality || 'xray',
      body_part: result.body_part || '',
      laterality: result.laterality || '',
      priority: result.priority || 'routine',
      clinical_history: result.clinical_history || '',
      notes: result.notes || '',
    })
    setFormOpen(true)
  }

  const handleView = async (result) => {
    try {
      const response = await api.get(`/clinical/imaging-results/${result.id}/`)
      setSelectedResult(response.data)
      setDetailOpen(true)
    } catch (error) {
      toast.error('Failed to load study details')
    }
  }

  const handleSave = async () => {
    if (!formData.patient_id || !formData.patient_name || !formData.study_name) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      if (selectedResult) {
        await api.patch(`/clinical/imaging-results/${selectedResult.id}/`, formData)
        toast.success('Imaging study updated')
      } else {
        await api.post('/clinical/imaging-results/', formData)
        toast.success('Imaging study created')
      }
      setFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving imaging study:', error)
      toast.error(error.response?.data?.error || 'Failed to save imaging study')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (result) => {
    if (!window.confirm(`Delete imaging study for ${result.patient_name}?`)) return
    try {
      await api.delete(`/clinical/imaging-results/${result.id}/`)
      toast.success('Imaging study deleted')
      fetchData()
    } catch (error) {
      console.error('Error deleting imaging study:', error)
      toast.error('Failed to delete imaging study')
    }
  }

  const handleCompleteStudy = async (result) => {
    try {
      await api.post(`/clinical/imaging-results/${result.id}/complete_study/`)
      toast.success('Study marked as completed')
      fetchData()
      setDetailOpen(false)
    } catch (error) {
      toast.error('Failed to complete study')
    }
  }

  const handleOpenReport = () => {
    setReportData({
      findings: selectedResult?.findings || '',
      impression: selectedResult?.impression || '',
      recommendations: selectedResult?.recommendations || '',
    })
    setReportOpen(true)
  }

  const handleSubmitReport = async () => {
    if (!reportData.findings || !reportData.impression) {
      toast.error('Please enter findings and impression')
      return
    }
    setSaving(true)
    try {
      await api.post(`/clinical/imaging-results/${selectedResult.id}/submit_report/`, reportData)
      toast.success('Report submitted successfully')
      setReportOpen(false)
      fetchData()
      setDetailOpen(false)
    } catch (error) {
      toast.error('Failed to submit report')
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async (result) => {
    try {
      await api.post(`/clinical/imaging-results/${result.id}/approve/`)
      toast.success('Imaging result approved')
      fetchData()
      setDetailOpen(false)
    } catch (error) {
      toast.error('Failed to approve imaging result')
    }
  }

  const getStatusColor = (status) => {
    const option = STATUS_OPTIONS.find((o) => o.value === status)
    return option?.color || 'default'
  }

  const getPriorityColor = (priority) => {
    const option = PRIORITY_OPTIONS.find((o) => o.value === priority)
    return option?.color || 'default'
  }

  const columns = [
    { field: 'patient_name', headerName: 'Patient Name', sortable: true },
    { field: 'patient_id', headerName: 'Patient ID' },
    { field: 'study_name', headerName: 'Study Name', sortable: true },
    {
      field: 'modality',
      headerName: 'Modality',
      renderCell: (v) => <Chip label={v?.toUpperCase()} size="small" />,
    },
    { field: 'body_part', headerName: 'Body Part' },
    {
      field: 'priority',
      headerName: 'Priority',
      renderCell: (v) => (
        <Chip
          label={v}
          size="small"
          color={getPriorityColor(v)}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (v) => (
        <Chip
          label={v?.replace('_', ' ')}
          size="small"
          color={getStatusColor(v)}
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'study_date',
      headerName: 'Study Date',
      renderCell: (v) => (v ? new Date(v).toLocaleDateString() : '-'),
      sortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      renderCell: (_, row) => (
        <Box display="flex" gap={0.5}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleView(row) }}>
            <Visibility fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEdit(row) }}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(row) }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <PageHeader
        title="Imaging Results"
        subtitle="Manage radiology and imaging study results"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Clinical', path: '/clinical/imaging-results' },
          { label: 'Imaging Results' },
        ]}
        actions={
          <Button startIcon={<Add />} onClick={handleAdd}>
            Add Imaging Study
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {pendingReportCount > 0 && (
          <Grid item xs={12} md={6}>
            <Alert severity="warning" icon={<Description />}>
              {pendingReportCount} study(ies) pending radiologist report
            </Alert>
          </Grid>
        )}
        {pendingApprovalCount > 0 && (
          <Grid item xs={12} md={6}>
            <Alert severity="info" icon={<CheckCircle />}>
              {pendingApprovalCount} study(ies) pending approval
            </Alert>
          </Grid>
        )}
      </Grid>

      <Card>
        <DataTable
          columns={columns}
          data={results}
          loading={loading}
          searchPlaceholder="Search imaging results..."
          emptyMessage="No imaging results found"
        />
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={selectedResult ? 'Edit Imaging Study' : 'Add Imaging Study'}
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>
              {selectedResult ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Patient ID"
              value={formData.patient_id}
              onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Patient Name"
              value={formData.patient_name}
              onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.patient_phone}
              onChange={(e) => setFormData({ ...formData, patient_phone: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.patient_gender}
                onChange={(e) => setFormData({ ...formData, patient_gender: e.target.value })}
                label="Gender"
              >
                <MenuItem value="">-</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Study Name"
              placeholder="e.g., Chest X-Ray, CT Abdomen"
              value={formData.study_name}
              onChange={(e) => setFormData({ ...formData, study_name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Modality</InputLabel>
              <Select
                value={formData.modality}
                onChange={(e) => setFormData({ ...formData, modality: e.target.value })}
                label="Modality"
              >
                {MODALITY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Body Part"
              placeholder="e.g., Chest, Abdomen, Head"
              value={formData.body_part}
              onChange={(e) => setFormData({ ...formData, body_part: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Laterality</InputLabel>
              <Select
                value={formData.laterality}
                onChange={(e) => setFormData({ ...formData, laterality: e.target.value })}
                label="Laterality"
              >
                <MenuItem value="">N/A</MenuItem>
                <MenuItem value="Left">Left</MenuItem>
                <MenuItem value="Right">Right</MenuItem>
                <MenuItem value="Bilateral">Bilateral</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                label="Priority"
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Clinical History"
              value={formData.clinical_history}
              onChange={(e) => setFormData({ ...formData, clinical_history: e.target.value })}
              multiline
              rows={3}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Detail Modal */}
      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Imaging Study Details"
        maxWidth="md"
        actions={
          <>
            {selectedResult?.status === 'scheduled' && (
              <Button color="info" onClick={() => handleCompleteStudy(selectedResult)}>
                Complete Study
              </Button>
            )}
            {selectedResult?.status === 'completed' && (
              <Button color="warning" onClick={handleOpenReport}>
                Submit Report
              </Button>
            )}
            {selectedResult?.status === 'reported' && (
              <Button color="success" onClick={() => handleApprove(selectedResult)}>
                Approve
              </Button>
            )}
            <Button variant="outlined" onClick={() => setDetailOpen(false)}>Close</Button>
          </>
        }
      >
        {selectedResult && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Patient Name</Box>
                <Box fontWeight={600}>{selectedResult.patient_name}</Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Patient ID</Box>
                <Box fontWeight={600}>{selectedResult.patient_id}</Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Study Name</Box>
                <Box fontWeight={600}>{selectedResult.study_name}</Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Modality</Box>
                <Chip label={selectedResult.modality?.toUpperCase()} size="small" sx={{ mt: 0.5 }} />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Body Part</Box>
                <Box fontWeight={600}>{selectedResult.body_part || '-'}</Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Box color="text.secondary" fontSize="0.75rem">Status</Box>
                <Chip
                  label={selectedResult.status?.replace('_', ' ')}
                  size="small"
                  color={getStatusColor(selectedResult.status)}
                  sx={{ textTransform: 'capitalize', mt: 0.5 }}
                />
              </Box>
            </Grid>
            {selectedResult.clinical_history && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Clinical History</Box>
                  <Box>{selectedResult.clinical_history}</Box>
                </Box>
              </Grid>
            )}
            {selectedResult.findings && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Findings</Box>
                  <Box>{selectedResult.findings}</Box>
                </Box>
              </Grid>
            )}
            {selectedResult.impression && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Impression</Box>
                  <Box fontWeight={600}>{selectedResult.impression}</Box>
                </Box>
              </Grid>
            )}
            {selectedResult.recommendations && (
              <Grid item xs={12}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Recommendations</Box>
                  <Box>{selectedResult.recommendations}</Box>
                </Box>
              </Grid>
            )}
            {selectedResult.technologist_name && (
              <Grid item xs={12} sm={6}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Technologist</Box>
                  <Box fontWeight={600}>{selectedResult.technologist_name}</Box>
                </Box>
              </Grid>
            )}
            {selectedResult.radiologist_name && (
              <Grid item xs={12} sm={6}>
                <Box>
                  <Box color="text.secondary" fontSize="0.75rem">Radiologist</Box>
                  <Box fontWeight={600}>{selectedResult.radiologist_name}</Box>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Modal>

      {/* Report Modal */}
      <Modal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        title="Submit Radiologist Report"
        maxWidth="md"
        actions={
          <>
            <Button variant="outlined" onClick={() => setReportOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitReport} loading={saving}>
              Submit Report
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Findings"
              value={reportData.findings}
              onChange={(e) => setReportData({ ...reportData, findings: e.target.value })}
              multiline
              rows={5}
              placeholder="Enter radiological findings..."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Impression"
              value={reportData.impression}
              onChange={(e) => setReportData({ ...reportData, impression: e.target.value })}
              multiline
              rows={3}
              placeholder="Enter clinical impression/conclusion..."
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Recommendations"
              value={reportData.recommendations}
              onChange={(e) => setReportData({ ...reportData, recommendations: e.target.value })}
              multiline
              rows={3}
              placeholder="Enter recommendations (if any)..."
            />
          </Grid>
        </Grid>
      </Modal>
    </Box>
  )
}

export default ImagingResultsList
